import { Reference } from "@apollo/client";
import { createContext, Dispatch, PropsWithChildren, SetStateAction, useContext, useEffect, useState } from "react";

import { openerContext } from "@context/Contexts";
import useGlobalOpener from "@hooks/useGlobalOpener";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyClient from "@hooks/useMyClient";
import useOpener, { Opener } from "@hooks/useOpener";
import { OpenerTypeEnum } from "@typing/Enums";
import {
  ChatCreateDocument,
  ChatCreateMutation,
  ChatCreateMutationVariables,
  ChatFragment,
  ChatFragmentDoc,
  GetJourneyForMessagesQuery,
  ResourceFragment,
  WorksheetFragment
} from "@typing/Generated";
import { sortByTimestamp } from "@utils/modelUtils";

export type MessageSearch = {
  chatId: string | null;
  found: boolean | null;
  messageId: string | null;
  searchTerm: string | null;
};

type InboxContextType = {
  editingChat: boolean;
  expandedOpener: Opener;
  handleChatCreate: () => void;
  handleMessageSearchFind: (chatId: string, messageId: string, searchTerm: string) => void;
  journey: GetJourneyForMessagesQuery["journey"];
  messageSearch: MessageSearch;
  onChatClientsChange: (clientIds: string[]) => void;
  opener: Opener;
  resourceData: ResourceFragment | undefined;
  selectedChat: ChatFragment | undefined;
  setEditingChat: Dispatch<SetStateAction<boolean>>;
  setMessageSearch: Dispatch<SetStateAction<MessageSearch>>;
  setResourceData: Dispatch<SetStateAction<ResourceFragment | undefined>>;
  setSelectedChat: Dispatch<SetStateAction<ChatFragment | undefined>>;
  setWorksheetData: Dispatch<SetStateAction<WorksheetFragment | undefined>>;
  worksheetData: WorksheetFragment | undefined;
};

export const InboxContext = createContext<InboxContextType | undefined>(undefined);

type Props = PropsWithChildren<{
  journey: GetJourneyForMessagesQuery["journey"];
}>;

const InboxProvider = ({ children, journey }: Props) => {
  const myClient = useMyClient();
  const expandedOpener = useOpener();
  const opener = useGlobalOpener(OpenerTypeEnum.MESSAGES);
  const queryParams = new URLSearchParams(location.search);
  const hideFab = queryParams.has("hideFab");
  const { openerState, toggleOpener } = useContext(openerContext);
  const messagingOpenerState = openerState.find(opener => opener.type === OpenerTypeEnum.MESSAGES);
  const messagingRecord = messagingOpenerState?.record;

  const [selectedChat, setSelectedChat] = useState<ChatFragment | undefined>(undefined);
  const [resourceData, setResourceData] = useState<ResourceFragment | undefined>();
  const [worksheetData, setWorksheetData] = useState<WorksheetFragment | undefined>();
  const [editingChat, setEditingChat] = useState<boolean>(false);
  const [messageSearch, setMessageSearch] = useState<MessageSearch>({
    chatId: null,
    found: null,
    messageId: null,
    searchTerm: null
  });

  const handleMessageSearchFind = (chatId: string, messageId: string, searchTerm: string) => {
    setSelectedChat(journey.chats.find(chat => chat.id === chatId));
    setMessageSearch({ chatId, found: false, messageId, searchTerm });
  };

  const [createChat] = useMutationWithValidation<ChatCreateMutation, ChatCreateMutationVariables>(
    ChatCreateDocument,
    "chat",
    {
      update: (store, { data }) => {
        if (data?.chatCreate?.chat) {
          store.modify({
            fields: {
              chats(cachedChatRefs: readonly Reference[]) {
                const newChatRef = store.writeFragment({
                  data: data.chatCreate?.chat,
                  fragment: ChatFragmentDoc,
                  fragmentName: "Chat"
                });
                if (newChatRef && data.chatCreate?.chat) {
                  return [...cachedChatRefs, newChatRef];
                }
                return [...cachedChatRefs];
              }
            },
            id: store.identify({ ...journey })
          });
        }
      }
    }
  );

  const handleChatCreate = () => {
    if (journey.chats.length > 0) {
      setSelectedChat(sortByTimestamp(journey.chats, chat => chat.createdAt).reverse()[0]);
      setEditingChat(true);
    } else if (myClient) {
      const clientIds = Array.from(new Set([myClient.id, journey.owner.id]));

      createChat({
        clientIds,
        journeyId: journey.id
      }).then(res => {
        if (res?.chatCreate?.chat) {
          setSelectedChat(res.chatCreate.chat);
          setEditingChat(true);
        }
      });
    }
  };

  const onChatClientsChange = (clientIds: string[]) => {
    const clientIdsWithMe = myClient ? [...clientIds, myClient.id] : clientIds;

    const existingChat = journey.chats.find(
      chat =>
        chat.clients.length === clientIdsWithMe.length &&
        chat.clients.map(client => client.id).every(clientId => clientIdsWithMe.includes(clientId))
    );

    if (existingChat) {
      setSelectedChat(existingChat);
      return;
    }
    if (clientIds.length > 0) {
      createChat({
        clientIds: clientIdsWithMe,
        journeyId: journey.id
      }).then(res => {
        if (res?.chatCreate?.chat) {
          setSelectedChat(res.chatCreate.chat);
        }
      });
    }
  };

  useEffect(() => {
    if (messagingRecord) {
      // When there is a messaging resource in the toggle state, take the data out of that resource and then clear the
      // state because we don't want the resource to persist as the messaging stuff gets closed and opened again.
      if (messagingRecord?.__typename === "Resource") {
        setResourceData(messagingRecord);
      } else if (messagingRecord?.__typename === "Worksheet") {
        setWorksheetData(messagingRecord);
      }
      toggleOpener(OpenerTypeEnum.MESSAGES, true, undefined);
    }
  }, [messagingRecord, toggleOpener]);

  useEffect(() => {
    if (messagingOpenerState?.isOpen === false) {
      setResourceData(undefined);
      setWorksheetData(undefined);
    }
  }, [messagingOpenerState?.isOpen]);

  // skip the inbox on load if there's only 1 chat and no chance of creating another one
  useEffect(() => {
    if (journey.clientRoles.length === 1 && journey.chats.length === 1 && !selectedChat) {
      setSelectedChat(journey.chats[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (hideFab) {
    return null;
  }

  const value: InboxContextType = {
    editingChat,
    expandedOpener,
    handleChatCreate,
    handleMessageSearchFind,
    journey,
    messageSearch,
    onChatClientsChange,
    opener,
    resourceData,
    selectedChat,
    setEditingChat,
    setMessageSearch,
    setResourceData,
    setSelectedChat,
    setWorksheetData,
    worksheetData
  };
  return <InboxContext.Provider value={value}>{children}</InboxContext.Provider>;
};

export default InboxProvider;
